/**
 * Spell Handler - Discrete handler for spells, slots, preparation, and casting
 * 
 * date_of_change: 2025-09-03
 * purpose: Implement single-responsibility spell handling per architectural governance
 * rule_reference: CHARACTER_MONSTER_ARCHITECTURE_ANALYSIS.md - Section 3: Discrete Handler Requirement
 */


import { CanonicalCharacter } from '../types/CanonicalSchemas';

export interface SpellValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  normalizedSpells?: CanonicalCharacter['spells'];
}

export interface SpellCalculationResult {
  spellAttackBonus: number;
  spellSaveDC: number;
  availableSlots: Record<number, { total: number; used: number; available: number }>;
  totalSpellsKnown: number;
  totalSpellsPrepared: number;
  cantripsKnown: number;
}

export interface CastingResult {
  success: boolean;
  spellName: string;
  level: number;
  slotUsed?: number;
  message: string;
  updatedSpells: CanonicalCharacter['spells'];
}

/**
 * Spell Handler - Manages spell data, slots, preparation, and casting exclusively
 * FORBIDDEN: Mixing with other data types (equipment, abilities, etc.)
 */
export class SpellHandler {
  
  /**
   * Validate spell data against canonical schema
   * @param spells - Spell data from CanonicalCharacter
   * @param characterLevel - Character level for slot validation
   * @returns Validation result with detailed errors/warnings
   */
  static async validate(
    spells?: CanonicalCharacter['spells'],
    characterLevel: number = 1
  ): Promise<SpellValidationResult> {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    if (!spells) {
      return { 
        valid: true, 
        errors: [], 
        warnings: [], 
        normalizedSpells: {} 
      };
    }
    
    const normalizedSpells: CanonicalCharacter['spells'] = {};
    const legacySpells = (spells || {}) as any;
    const normalizeAbility = (value: unknown) => {
      const normalized = String(value || '').trim().toLowerCase();
      if (!normalized) return null;
      if (normalized === 'int') return 'intelligence';
      if (normalized === 'wis') return 'wisdom';
      if (normalized === 'cha') return 'charisma';
      return ['intelligence', 'wisdom', 'charisma'].includes(normalized)
        ? (normalized as CanonicalCharacter['spells']['ability'])
        : null;
    };
    const pickSpellArray = (...candidates: unknown[]) => {
      let firstArray: unknown[] | null = null;
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
          if (!firstArray) {
            firstArray = candidate;
          }
          if (candidate.length > 0) {
            return candidate;
          }
        }
      }
      return firstArray;
    };
    
    // Validate spellcasting ability
    const normalizedAbility = normalizeAbility(spells.ability || legacySpells.spellcastingAbility || legacySpells.spellAbility);
    if (spells.ability || legacySpells.spellcastingAbility || legacySpells.spellAbility) {
      if (!normalizedAbility) {
        errors.push('Spellcasting ability must be intelligence, wisdom, or charisma');
      } else {
        normalizedSpells.ability = normalizedAbility;
      }
    }
    
    // Validate attack bonus
    const attackBonus = spells.attackBonus ?? legacySpells.spellAttackBonus;
    if (attackBonus !== undefined) {
      if (typeof attackBonus !== 'number') {
        errors.push('Spell attack bonus must be a number');
      } else {
        normalizedSpells.attackBonus = attackBonus;
      }
    }
    
    // Validate save DC
    const saveDC = spells.saveDC ?? legacySpells.saveDc ?? legacySpells.spellSaveDC;
    if (saveDC !== undefined) {
      if (typeof saveDC !== 'number' || saveDC < 8) {
        errors.push('Spell save DC must be a number (minimum 8)');
      } else {
        normalizedSpells.saveDC = saveDC;
      }
    }
    
    // Validate known spells
    // @date_of_change 2025-09-13 - FULL EMBED: Accept both string IDs and full spell objects
    const knownSpells = pickSpellArray(spells.known, legacySpells.spellList, legacySpells.spellsKnown, legacySpells.spells);
    if (knownSpells) {
      if (!Array.isArray(knownSpells)) {
        errors.push('Known spells must be an array');
      } else {
        normalizedSpells.known = knownSpells.filter(spell => 
          (typeof spell === 'string' && spell.trim().length > 0) ||
          (typeof spell === 'object' && spell !== null)
        ) as any;
      }
    }
    
    // Validate prepared spells
    // @date_of_change 2025-09-13 - FULL EMBED: Accept both string IDs and full spell objects
    if (spells.prepared) {
      if (!Array.isArray(spells.prepared)) {
        errors.push('Prepared spells must be an array');
      } else {
        normalizedSpells.prepared = spells.prepared.filter(spell => 
          (typeof spell === 'string' && spell.trim().length > 0) ||
          (typeof spell === 'object' && spell !== null)
        ) as any;
      }
    }
    
    // Validate spells by level
    // @date_of_change 2025-09-13 - FULL EMBED: Accept both string IDs and full spell objects
    if (spells.byLevel) {
      if (typeof spells.byLevel !== 'object') {
        errors.push('Spells byLevel must be an object');
      } else {
        normalizedSpells.byLevel = {};
        for (const [level, spellList] of Object.entries(spells.byLevel)) {
          const levelNum = parseInt(level, 10);
          if (isNaN(levelNum) || levelNum < 0 || levelNum > 9) {
            errors.push(`Invalid spell level: ${level} (must be 0-9)`);
            continue;
          }
          
          if (Array.isArray(spellList)) {
            normalizedSpells.byLevel[levelNum] = (spellList as any[]).filter(spell => 
              (typeof spell === 'string' && spell.trim().length > 0) ||
              (typeof spell === 'object' && spell !== null)
            ) as any[];
          } else {
            warnings.push(`Spells for level ${level} should be an array`);
          }
        }
      }
    }
    
    // Validate spell slots
    const slotSource = spells.slots || legacySpells.spellSlots;
    if (slotSource) {
      if (typeof slotSource !== 'object') {
        errors.push('Spell slots must be an object');
      } else {
        normalizedSpells.slots = {};
        for (const [level, slotData] of Object.entries(slotSource)) {
          const levelNum = parseInt(level, 10);
          if (isNaN(levelNum) || levelNum < 1 || levelNum > 9) {
            errors.push(`Invalid slot level: ${level} (must be 1-9)`);
            continue;
          }

          if (typeof slotData === 'number') {
            if (slotData < 0) {
              errors.push(`Slot level ${level}: total must be a non-negative number`);
              continue;
            }

            normalizedSpells.slots[levelNum] = { total: slotData, used: 0 };
            continue;
          }

          if (typeof slotData === 'object' && slotData !== null) {
            const { total, used } = slotData as { total: number; used: number };
            if (typeof total !== 'number' || total < 0) {
              errors.push(`Slot level ${level}: total must be a non-negative number`);
              continue;
            }
            if (typeof used !== 'number' || used < 0 || used > total) {
              errors.push(`Slot level ${level}: used must be between 0 and total`);
              continue;
            }
            
            normalizedSpells.slots[levelNum] = { total, used };
          }
        }
      }
    }
    
    // Validate at-will spells
    // @date_of_change 2025-09-13 - FULL EMBED: Accept both string IDs and full spell objects
    if (spells.atWill) {
      if (!Array.isArray(spells.atWill)) {
        errors.push('At-will spells must be an array');
      } else {
        normalizedSpells.atWill = spells.atWill.filter(spell => 
          (typeof spell === 'string' && spell.trim().length > 0) ||
          (typeof spell === 'object' && spell !== null)
        ) as any;
      }
    }
    
    // Validate per-day spells
    if (spells.perDay) {
      if (!Array.isArray(spells.perDay)) {
        errors.push('Per-day spells must be an array');
      } else {
        normalizedSpells.perDay = spells.perDay.filter(entry => 
          typeof entry === 'object' && 
          typeof entry.times === 'number' && 
          Array.isArray(entry.spells)
        );
      }
    }
    
    // Validate resolved references
    if (spells.resolved) {
      if (!Array.isArray(spells.resolved)) {
        warnings.push('Resolved references should be an array');
      } else {
        normalizedSpells.resolved = spells.resolved;
      }
    }

    // Enforce typed damage on embedded spell objects (no fallbacks)
    // date_of_change: 2025-11-05
    // rule_reference: AGENTS.md §9 No fallbacks; require typed damage on ingest
    const scanSpellArray = (arr: any[], label: string) => {
      if (!Array.isArray(arr)) return;
      for (const s of arr) {
        if (!s || typeof s !== 'object') continue;
        const name = String((s as any).name || 'spell');
        if (Array.isArray((s as any).damage)) {
          for (const d of (s as any).damage) {
            if (!d || typeof d !== 'object') { errors.push(`${label}:${name} damage entry invalid`); continue; }
            const t = (d as any).type;
            if (typeof t !== 'string' || t.trim().length === 0) {
              errors.push(`${label}:${name} damage component missing type`);
            }
          }
        } else if ((s as any).damage) {
          // string formula path requires explicit damageType
          const dtype = (s as any).damageType;
          if (typeof dtype !== 'string' || dtype.trim().length === 0) {
            errors.push(`${label}:${name} damageType is required when damage is provided`);
          }
        }
      }
    };

    try {
      scanSpellArray(spells.known as any[], 'known');
      scanSpellArray(spells.prepared as any[], 'prepared');
      scanSpellArray(spells.atWill as any[], 'atWill');
      if (spells.byLevel && typeof spells.byLevel === 'object') {
        for (const [lvl, arr] of Object.entries(spells.byLevel)) {
          scanSpellArray(arr as any[], `byLevel[${lvl}]`);
        }
      }
      if (Array.isArray(spells.perDay)) {
        for (const entry of spells.perDay) {
          if (entry && Array.isArray((entry as any).spells)) {
            scanSpellArray((entry as any).spells, 'perDay');
          }
        }
      }
    } catch (_) {}
    
    return {
      valid: errors.length === 0,
      errors,
      warnings,
      normalizedSpells
    };
  }
  
  /**
   * Calculate spell-derived values and statistics
   * @param spells - Validated canonical spell data
   * @param characterLevel - Character level
   * @param spellcastingAbilityScore - Score for spellcasting ability
   * @param proficiencyBonus - Character's proficiency bonus
   * @returns Calculated spell statistics
   */
  static calculateStats(
    spells: CanonicalCharacter['spells'],
    characterLevel: number,
    spellcastingAbilityScore: number,
    proficiencyBonus: number
  ): SpellCalculationResult {
    
    const abilityModifier = Math.floor((spellcastingAbilityScore - 10) / 2);
    
    // Calculate spell attack bonus and save DC
    const spellAttackBonus = spells?.attackBonus || (proficiencyBonus + abilityModifier);
    const spellSaveDC = spells?.saveDC || (8 + proficiencyBonus + abilityModifier);
    
    // Calculate available slots
    const availableSlots: Record<number, { total: number; used: number; available: number }> = {};
    if (spells?.slots) {
      for (const [level, slotData] of Object.entries(spells.slots)) {
        const levelNum = parseInt(level, 10);
        availableSlots[levelNum] = {
          total: slotData.total,
          used: slotData.used,
          available: slotData.total - slotData.used
        };
      }
    }
    
    // Count spells
    const totalSpellsKnown = spells?.known?.length || 0;
    const totalSpellsPrepared = spells?.prepared?.length || 0;
    const cantripsKnown = spells?.byLevel?.[0]?.length || spells?.atWill?.length || 0;
    
    return {
      spellAttackBonus,
      spellSaveDC,
      availableSlots,
      totalSpellsKnown,
      totalSpellsPrepared,
      cantripsKnown
    };
  }
  
  /**
   * Cast a spell, consuming appropriate spell slot
   * @param spells - Current spell data
   * @param spellName - Name of spell to cast
   * @param spellLevel - Level to cast the spell at
   * @param useSlot - Whether to consume a spell slot
   * @returns Casting result with updated spell data
   */
  static castSpell(
    spells: CanonicalCharacter['spells'],
    spellName: string,
    spellLevel: number,
    useSlot: boolean = true
  ): CastingResult {
    
    if (!spells) {
      return {
        success: false,
        spellName,
        level: spellLevel,
        message: 'No spellcasting data available',
        updatedSpells: {}
      };
    }
    
    // @date_of_change 2025-09-13 - ENHANCED DEBUGGING: Track spell validation in detail
    console.log(`🪄 [SPELL_VALIDATION_DETAILED] Validating ${spellName} (level ${spellLevel}):`, {
      spellName,
      spellLevel,
      isCantrip: spellLevel === 0,
      spellsStructure: {
        known: spells.known || [],
        knownCount: spells.known ? spells.known.length : 0,
        prepared: spells.prepared || [],
        preparedCount: spells.prepared ? spells.prepared.length : 0,
        atWill: spells.atWill || [],
        atWillCount: spells.atWill ? spells.atWill.length : 0,
        byLevel: spells.byLevel || {},
        byLevelKeys: spells.byLevel ? Object.keys(spells.byLevel) : [],
        // Show actual contents for debugging
        knownContents: spells.known ? JSON.stringify(spells.known) : 'null',
        preparedContents: spells.prepared ? JSON.stringify(spells.prepared) : 'null',
        level1Contents: spells.byLevel?.[1] ? JSON.stringify(spells.byLevel[1]) : 'null'
      }
    });

    // Check if spell is known/prepared
    const isCantrip = spellLevel === 0;

    // @date_of_change 2025-09-19 - ENHANCED FIX: Add spell name normalization for hyphen/space variants
    const normalizeSpellName = (name: string): string => {
      return name.toLowerCase().replace(/[-\s]+/g, '-'); // Convert spaces to hyphens, normalize multiple chars to single hyphen
    };

    // Enhanced comparison function that handles both formats
    const compareSpellNames = (spell1: string, spell2: string): boolean => {
      return normalizeSpellName(spell1) === normalizeSpellName(spell2);
    };
    const isKnown = spells.known?.some(spell =>
      (typeof spell === 'string' ? compareSpellNames(spell, spellName) :
       typeof spell === 'object' && spell !== null && compareSpellNames((spell as any)?.name || '', spellName))
    ) || false;
    const isPrepared = spells.prepared?.some(spell =>
      (typeof spell === 'string' ? compareSpellNames(spell, spellName) :
       typeof spell === 'object' && spell !== null && compareSpellNames((spell as any)?.name || '', spellName))
    ) || false;
    const isAtWill = spells.atWill?.some(spell =>
      (typeof spell === 'string' ? compareSpellNames(spell, spellName) :
       typeof spell === 'object' && spell !== null && compareSpellNames((spell as any)?.name || '', spellName))
    ) || false;

    // @date_of_change 2025-09-19 - ENHANCED FIX: Check spells in byLevel arrays with normalized name matching
    const checkSpellInLevel = (levelArray: any[], targetName: string): boolean => {
      if (!Array.isArray(levelArray)) return false;
      return levelArray.some((spell: any) => {
        if (typeof spell === 'string') return compareSpellNames(spell, targetName);
        if (typeof spell === 'object' && spell !== null && spell.name) return compareSpellNames(spell.name, targetName);
        return false;
      });
    };

    const isInCantripsArray = isCantrip && checkSpellInLevel(spells.byLevel?.[0] || [], spellName);
    const isInByLevelArray = !isCantrip && checkSpellInLevel(spells.byLevel?.[spellLevel] || [], spellName);

    console.log(`🪄 [SPELL_VALIDATION_CHECKS] Spell checks for ${spellName}:`, {
      isCantrip,
      isKnown,
      isPrepared,
      isAtWill,
      isInCantripsArray,
      isInByLevelArray,
      finalAllowed: isCantrip ? (isInCantripsArray || isKnown || isPrepared || isAtWill) : (isInByLevelArray || isKnown || isPrepared || isAtWill)
    });

    // For cantrips: allow if it's in byLevel[0], known, prepared, or atWill
    // For non-cantrips: allow if it's in byLevel[spellLevel], known, prepared, or atWill
    if (isCantrip) {
      if (!isInCantripsArray && !isKnown && !isPrepared && !isAtWill) {
        console.error(`🪄 [SPELL_VALIDATION_FAILED] Cantrip ${spellName} not found in any valid location`);
        return {
          success: false,
          spellName,
          level: spellLevel,
          message: 'Cantrip is not known',
          updatedSpells: spells
        };
      }
    } else {
      if (!isInByLevelArray && !isKnown && !isPrepared && !isAtWill) {
        console.error(`🪄 [SPELL_VALIDATION_FAILED] Spell ${spellName} not found in byLevel[${spellLevel}]/known/prepared/atWill`);
        return {
          success: false,
          spellName,
          level: spellLevel,
          message: 'Spell is not known or prepared',
          updatedSpells: spells
        };
      }
    }
    
    // Handle cantrips and at-will spells (no slot required)
    if (isCantrip || isAtWill) {
      return {
        success: true,
        spellName,
        level: spellLevel,
        message: `Cast ${spellName} (no slot consumed)`,
        updatedSpells: spells
      };
    }
    
    // Check and consume spell slot
    if (useSlot && spells.slots) {
      const slotsAtLevel = spells.slots[spellLevel];
      if (!slotsAtLevel || slotsAtLevel.used >= slotsAtLevel.total) {
        return {
          success: false,
          spellName,
          level: spellLevel,
          message: `No spell slots available at level ${spellLevel}`,
          updatedSpells: spells
        };
      }
      
      // Consume slot and return updated data
      const updatedSpells: CanonicalCharacter['spells'] = {
        ...spells,
        slots: {
          ...spells.slots,
          [spellLevel]: {
            total: slotsAtLevel.total,
            used: slotsAtLevel.used + 1
          }
        }
      };
      
      return {
        success: true,
        spellName,
        level: spellLevel,
        slotUsed: spellLevel,
        message: `Cast ${spellName} using level ${spellLevel} slot`,
        updatedSpells
      };
    }
    
    return {
      success: true,
      spellName,
      level: spellLevel,
      message: `Cast ${spellName}`,
      updatedSpells: spells
    };
  }
  
  /**
   * Restore spell slots during rest
   * @param spells - Current spell data
   * @param restType - 'short' or 'long' rest
   * @returns Updated spell data with restored slots
   */
  static restoreSpellSlots(
    spells: CanonicalCharacter['spells'],
    restType: 'short' | 'long'
  ): CanonicalCharacter['spells'] {
    
    if (!spells?.slots) return spells;
    
    if (restType === 'long') {
      // Long rest: restore all spell slots
      const restoredSlots = Object.fromEntries(
        Object.entries(spells.slots).map(([level, slotData]) => [
          level,
          { total: slotData.total, used: 0 }
        ])
      );
      
      return {
        ...spells,
        slots: restoredSlots
      };
    }
    
    // Short rest: only some classes get slot recovery (handled by class features)
    return spells;
  }
  
  /**
   * Get spell data for human-readable display
   * Returns canonical format with no transformation
   */
  static getDisplayData(spells?: CanonicalCharacter['spells']): CanonicalCharacter['spells'] {
    // ZERO TRANSFORMATION RULE: Return canonical format directly
    return spells || {};
  }
  
  /**
   * Prepare spell data for BattleRoom integration
   * Returns canonical format with calculated stats
   */
  static prepareBattleRoomData(
    spells: CanonicalCharacter['spells'],
    characterLevel: number,
    spellcastingAbilityScore: number,
    proficiencyBonus: number
  ): {
    spells: CanonicalCharacter['spells'];
    calculations: SpellCalculationResult;
  } {
    
    const calculations = this.calculateStats(
      spells, 
      characterLevel, 
      spellcastingAbilityScore, 
      proficiencyBonus
    );
    
    return {
      spells: spells || {}, // ZERO TRANSFORMATION: Canonical format only
      calculations
    };
  }

  /**
   * SpellResolver - Classify a spell into a structured, engine-friendly description.
   * @date_of_change 2025-09-14
   */
  static resolve(spell: any, castLevel: number = 1): SpellResolutionMeta | null {
    if (!spell) return null;
    const name = String(spell.name || '').toLowerCase();

    if (name.includes('magic missile')) {
      const meta: SpellResolutionMeta = {
        kind: 'auto_hit_multi_dart',
        engineSupported: true,
        targeting: 'creature',
        range: typeof spell.rangeFeet === 'number' ? spell.rangeFeet : undefined,
        requiresConcentration: false,
        slotLevel: castLevel,
        multiComponent: {
          baseCount: 3,
          perSlot: 1,
          targeting: 'per_component',
          perComponent: {
            autoHit: true,
            requiresAttackRoll: false,
            formula: '1d4+1',
            type: 'force'
          }
        },
        upcast: {
          baseLevel: 1,
          prompt: true
        },
        slotScaling: 'per_slot_component'
      };
      return meta;
    }

    if (name.includes('bless')) {
      const meta: SpellResolutionMeta = {
        kind: 'buff',
        engineSupported: true,
        targeting: 'creature',
        range: typeof spell.rangeFeet === 'number' ? spell.rangeFeet : 30,
        requiresConcentration: true,
        slotLevel: castLevel,
        allowSelf: true,
        upcast: {
          baseLevel: 1,
          extraTargetsPerSlot: 1,
          prompt: true
        },
        multiComponent: {
          baseCount: 3,
          perSlot: 1,
          targeting: 'per_component',
          perComponent: {
            autoHit: true,
            requiresAttackRoll: false
          }
        },
        slotScaling: 'per_slot_component'
      };
      return meta;
    }

    if (name.includes('detect magic')) {
      // @date_of_change: 2025-10-27 - Detect Magic is a self-range concentration utility with 10-minute duration
      // rule_reference: PHB 2024, Detect Magic – Range: Self (30 ft aura), Concentration up to 10 minutes
      const meta: SpellResolutionMeta = {
        kind: 'utility',
        engineSupported: true,
        targeting: 'self',
        range: 0,
        requiresConcentration: true,
        slotLevel: castLevel,
        durationSeconds: 600,
        durationRounds: 100,
        allowSelf: true
      } as SpellResolutionMeta;
      return meta;
    }

    if (name.includes('invisibility')) {
      const meta: SpellResolutionMeta = {
        kind: 'utility',
        targeting: 'creature',
        range: typeof spell.rangeFeet === 'number' ? spell.rangeFeet : 5, // Touch
        requiresConcentration: true,
        slotLevel: castLevel,
        allowSelf: true, // Touch can include self
        upcast: {
          baseLevel: 2,
          extraTargetsPerSlot: 1,
          prompt: true
        },
        slotScaling: 'per_slot_component'
      };
      return meta;
    }

    return null;
  }
}

/**
 * SpellResolutionMeta — structured metadata returned by SpellHandler.resolve()
 * This is intentionally minimal and forward-compatible.
 */
export interface SpellResolutionMeta {
  /** coarse classification the engine can branch on */
  kind:
    | 'auto_hit_multi_dart'    // e.g., Magic Missile
    | 'attack_roll_multi'      // e.g., Scorching Ray
    | 'attack_roll_single'
    | 'save_single'
    | 'save_aoe'
    | 'buff'
    | 'debuff'
    | 'utility'
    | 'unknown';
  targeting: 'creature' | 'point' | 'self' | 'line' | 'cone' | 'sphere' | 'cube' | 'cylinder' | 'unknown';
  /** true only when VCS has a concrete resolver for this spell's effects */
  engineSupported?: boolean;
  range?: number; // in feet, if known
  requiresConcentration?: boolean;
  slotLevel?: number; // casting slot level actually used
  slotScaling?: 'per_slot_component' | 'per_slot_dice' | 'per_slot_flat' | 'none';
  // Whether the caster can target themselves when targeting creatures
  allowSelf?: boolean;
  // Upcast metadata (if spell supports it)
  upcast?: {
    baseLevel: number;
    extraTargetsPerSlot?: number; // e.g., Invisibility: +1 creature per level above base
    prompt?: boolean;             // UI should ask whether to upcast
  };
  multiComponent?: {
    baseCount: number;        // components at base level
    perSlot?: number;         // added components per slot level above base (usually 1)
    targeting?: 'per_component' | 'shared';
    perComponent: {
      autoHit?: boolean;
      requiresAttackRoll?: boolean;
      formula?: string;       // e.g., '1d4+1'
      type?: string;          // e.g., 'force'
    }
  };
  durationSeconds?: number;
  durationRounds?: number;
}
